import { FunctionComponent, ReactNode, useEffect, useMemo, useState } from 'react';

import {
  StreamConnected as CoreStreamConnected,
  useGameStateSlice,
  useIsPortrait,
  useIsLargerThanWide,
  useModifiers,
  useBetSlice,
  useSettingsSlice,
  VideoPlayer,
  DeviceInfo,
} from '@pp/core';

import { useGameDispatch } from '../../store/GameStore';
import { useMWGameGridSlice } from '../../store/slices/mwGameGrid/selectors';
import { mwGameGridActions } from '../../store/slices/mwGameGrid/slice';
import { useGameGridContext } from '../gameGrid/GameGridContext';

import styles from './stream.module.css';
import { useIsPortraitMax } from '../../hooks/useWindowDimension';

export interface StreamConnectedProps {}

export const StreamConnected: FunctionComponent<StreamConnectedProps> = () => {
  const dispatch = useGameDispatch();
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const balance = useGameStateSlice.getDeductibleBalance();
  const isLowBalance = useBetSlice.isLowBalance(balance);
  const bettingGridScale = useMWGameGridSlice.getDimension('bettingGridScale');
  const bettingGridHeight = useMWGameGridSlice.getDimension('bettingGridHeight');
  const playAreaSize = useMWGameGridSlice.getDimension('playAreaHeight');
  const [poster, setPoster] = useState('');
  const classic = useSettingsSlice.getIsInClassicView();
  const controlsOffsetBottom = useMWGameGridSlice.getDimension('controlsOffsetBottom') || 0;
  const [minAspectRatio, setMinAspectRatio] = useState(0);
  const isNotCollapsed = isBetsOpenStatus && !isLowBalance;

  const {
    refs: { gameGridWrapperRef },
  } = useGameGridContext();

  const isLoadedInActiveRound = useGameStateSlice.isLoadedInActiveRound();

  const isPortrait = useIsPortrait();
  const isPortraitMax = useIsPortraitMax();
  const isWide = useIsLargerThanWide();

  const mods = useModifiers(styles, 'stream', { expanded: !isNotCollapsed, classic });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setPoster(window.videoPoster);
  }, [isLoadedInActiveRound]);

  const bottomAllowedCrop = useMemo(() => {
    return isWide || isPortrait ? 0.18 : 0.5;
  }, [isWide, isPortrait]);

  const bottomOffset = useMemo(() => {
    if (!bettingGridHeight || !bettingGridScale || !playAreaSize) return 0;
    let bottomOffset = 0;
    if (isWide) {
      return controlsOffsetBottom;
    }
    if (!isPortrait) {
      bottomOffset = controlsOffsetBottom + (isNotCollapsed ? bettingGridHeight : bettingGridHeight * bettingGridScale);
    } else {
      bottomOffset = playAreaSize - (bettingGridHeight * (1 - bettingGridScale) + 54);
    }
    if (!bottomOffset) return bottomOffset;
    return Math.max(bottomOffset, controlsOffsetBottom + (isWide ? 50 : 0));
  }, [controlsOffsetBottom, isWide, isPortrait, isNotCollapsed, bettingGridHeight, bettingGridScale, playAreaSize]);

  const overlayHeight = useMemo(() => {
    // As per comment by Nikolajs https://pragmaticplay.atlassian.net/browse/LCE-62557?focusedCommentId=668754
    if (!bettingGridHeight || !playAreaSize) return undefined;
    let overlayHeight = bottomOffset;
    if (isWide) {
      overlayHeight = isNotCollapsed ? playAreaSize : playAreaSize - bettingGridHeight;
    }
    return Math.max(overlayHeight, controlsOffsetBottom + (isWide ? 50 : 0));
  }, [bottomOffset, isWide, controlsOffsetBottom, isNotCollapsed, playAreaSize, bettingGridHeight]);

  const handleAspectRatio = (): void => {
    const defaultAspectRatio = 1.75;
    const aspectRatioMin = 1.4;
    if (isPortrait) {
      if (!bettingGridHeight || !bettingGridScale || !playAreaSize || !gameGridWrapperRef.current) {
        setMinAspectRatio(defaultAspectRatio);
        return;
      }
      const videoAspectRatio = defaultAspectRatio;
      const videoHeight = (gameGridWrapperRef.current?.offsetWidth || 0) / videoAspectRatio;
      const xHeight = (gameGridWrapperRef.current?.offsetHeight || 0) - videoHeight;
      const isPortraitMaxVal = isPortraitMax ? 0 : 20;
      const betStateOffset = isNotCollapsed ? bettingGridHeight * (1 - bettingGridScale) - 50 : 10 + isPortraitMaxVal;
      const factor = (xHeight - betStateOffset) / (playAreaSize || 1);
      const aspectRatio = Math.round(Math.min(defaultAspectRatio, aspectRatioMin / factor) * 100) / 100;
      const calculatedAspectRatio = factor > 1 ? aspectRatioMin : aspectRatio;

      setMinAspectRatio(calculatedAspectRatio);
    } else if (isWide) {
      setMinAspectRatio(1.67);
    } else {
      setMinAspectRatio(defaultAspectRatio);
    }
  };
  useEffect(() => {
    const isIOS = DeviceInfo.getPlatform().IS_IOS;
    if (isIOS && isPortrait) {
      //iOS Safari Quirks: to get the updated value of offsetWidth we are using delay here.Issue with only iphone 14 with safari browser.
      setTimeout(() => {
        requestAnimationFrame(handleAspectRatio);
      }, 300);
    } else {
      handleAspectRatio();
    }
  }, [
    isWide,
    isPortrait,
    isPortraitMax,
    playAreaSize,
    isNotCollapsed,
    bettingGridHeight,
    bettingGridScale,
    gameGridWrapperRef.current?.offsetHeight,
    gameGridWrapperRef.current?.offsetWidth,
  ]);

  return (
    <CoreStreamConnected
      className={`${styles.stream} ${mods}`}
      curtainBase={<div className={`${styles.stream__videoCurtainBase}`} />}
      curtainOverlay={<div className={`${styles.stream__videoCurtainOverlay}`} />}
      overlay={<div className={`${styles.stream__overlay}`} />}
      overlayFade={<div className={`${styles.stream__overlayFade}`} />}
      onResize={(size): void => {
        dispatch(mwGameGridActions.handleStreamResize(size));
      }}
      onViewableAreaChange={(viewableArea): void => {
        dispatch(mwGameGridActions.setStreamViewableHeight(viewableArea));
      }}
      minAspectRatio={minAspectRatio}
      bottomAllowedCrop={bottomAllowedCrop}
      bottomOffset={bottomOffset}
      overlayHeight={overlayHeight}
      adaptOverlayHeight={false}
      gameContainerRef={gameGridWrapperRef}
      renderVideo={(rest): ReactNode => {
        return <VideoPlayer poster={poster} {...rest} />;
      }}
    />
  );
};
